import React, { ReactNode, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { faDownload, faEdit, faPaperPlane, faPlus } from '@fortawesome/free-solid-svg-icons'
import Table, {
  TableBody,
  TableData,
  TableHeader,
  TableHeaderItem,
  TableRow,
} from 'components/Table'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import FillingSpinner from 'components/FillingSpinner'
import { AppContext } from 'contexts/AppContext'
import { Disposition, DispositionCorrection } from 'types/disposition'
import DateUtils from 'utils/date'
import { DATE_FORMAT, DATE_HOUR_FORMAT } from 'constant'
import CorrectionModal from '../CorrectionModal'
import service from '../service'

interface DispositionDetailsProps {
  disposition: Disposition
  onChanged: () => void
}

const DispositionDetails: React.FC<DispositionDetailsProps> = ({ disposition, onChanged }) => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const [editedCorrection, setEditedCorrection] = useState<DispositionCorrection | null>(null)
  const [sending, setSending] = useState<boolean>(false)

  const hasUnsentCorrection = service.hasUnsentCorrection(disposition)
  const hasInvoiceNumber = Boolean(disposition.invoiceNumber)
  let addCorrectionTooltip: string | undefined = undefined
  if (!hasInvoiceNumber) {
    addCorrectionTooltip = t('agencies.dispositions.corrections.addBlockedNoInvoice')
  } else if (hasUnsentCorrection) {
    addCorrectionTooltip = t('agencies.dispositions.corrections.addBlocked')
  }

  const openModal = (correction: DispositionCorrection | null): void => {
    setEditedCorrection(correction)
    setIsModalOpen(true)
  }

  const sendCorrection = (correction: DispositionCorrection): void => {
    setSending(true)
    void service
      .sendCorrection(correction.id, addNotification, t)
      .then(success => success && onChanged())
      .finally(() => setSending(false))
  }

  const field = (label: string, value: ReactNode): ReactNode => (
    <div
      className='DispositionDetails__field'
      key={label}
    >
      <span className='DispositionDetails__field-label'>{label}</span>
      <span className='DispositionDetails__field-value'>{value}</span>
    </div>
  )

  const fields: ReactNode = (
    <div className='DispositionDetails__fields'>
      {field(t('agencies.dispositions.customerName'), disposition.customerName)}
      {field(t('agencies.dispositions.customerAddress'), disposition.customerAddress)}
      {field(t('agencies.form.nip'), disposition.nip)}
      {field(t('common.invoiceEmail'), disposition.invoiceEmail)}
      {field(t('agencies.dispositions.invoiceNumber'), disposition.invoiceNumber || '—')}
      {field(t('agencies.dispositions.productName'), disposition.productName)}
      {field(
        t('agencies.dispositions.paidDate'),
        DateUtils.parseAndFormat(disposition.paidDate, DATE_FORMAT)
      )}
      {field(
        t('agencies.dispositions.dueDate'),
        t('agencies.dispositions.dueDateDays', { number: disposition.dueDate })
      )}
      {field(t('agencies.dispositions.paymentMethod'), disposition.paymentMethod)}
      {field(t('agencies.dispositions.quantity'), disposition.quantity)}
      {field(t('agencies.dispositions.price'), service.formatPrice(disposition.price))}
      {field(t('agencies.dispositions.serviceId'), disposition.serviceId)}
      {field(t('agencies.dispositions.accountType'), disposition.accountType)}
      {field(t('agencies.dispositions.accountValue'), disposition.accountValue)}
      {field(t('agencies.dispositions.signature'), disposition.signature)}
    </div>
  )

  const correctionRows: ReactNode = disposition.corrections.map(correction => (
    <TableRow key={correction.id}>
      <TableData>{DateUtils.parseAndFormat(correction.createdAt, DATE_HOUR_FORMAT)}</TableData>
      <TableData>{service.formatPercent(correction.percentChange)}</TableData>
      <TableData>{service.formatPrice(correction.originalPrice)}</TableData>
      <TableData>{service.formatPrice(correction.correctedPrice)}</TableData>
      <TableData>{correction.correctionReason}</TableData>
      <TableData>
        {correction.sentAt
          ? t('agencies.dispositions.corrections.sentAt', {
              date: DateUtils.parseAndFormat(correction.sentAt, DATE_HOUR_FORMAT),
            })
          : t('agencies.dispositions.corrections.notSent')}
      </TableData>
      <TableData>
        <div className='DispositionDetails__actions'>
          <Button
            size={ButtonSize.SMALL}
            theme={ButtonTheme.NAVY_BLUE_OUTLINE}
            icon={faDownload}
            tooltip={t('agencies.dispositions.downloadCorrection')}
            disabled={!correction.fileUrl}
            onClick={() =>
              service.downloadFile(correction.fileUrl, service.correctionFileName(correction))
            }
          />
          {!correction.sentAt && (
            <>
              <Button
                dataCy='correction-edit'
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                icon={faEdit}
                tooltip={t('common.edit')}
                disabled={sending}
                onClick={() => openModal(correction)}
              />
              <Button
                dataCy='correction-send'
                size={ButtonSize.SMALL}
                theme={ButtonTheme.NAVY_BLUE_OUTLINE}
                icon={faPaperPlane}
                tooltip={t('agencies.dispositions.corrections.send')}
                disabled={sending}
                onClick={() => sendCorrection(correction)}
              />
            </>
          )}
        </div>
      </TableData>
    </TableRow>
  ))

  return (
    <div className='DispositionDetails'>
      {fields}

      <div className='DispositionDetails__corrections-header'>
        <h4>{t('agencies.dispositions.corrections.header')}</h4>
        <div className='DispositionDetails__corrections-buttons'>
          <Button
            dataCy='correction-add'
            size={ButtonSize.SMALL}
            icon={faPlus}
            disabled={sending || hasUnsentCorrection || !hasInvoiceNumber}
            tooltip={addCorrectionTooltip}
            onClick={() => openModal(null)}
          >
            {t('agencies.dispositions.corrections.add')}
          </Button>
        </div>
      </div>

      {sending && <FillingSpinner />}

      {disposition.corrections.length === 0 ? (
        <div className='DispositionDetails__empty'>
          {t('agencies.dispositions.corrections.empty')}
        </div>
      ) : (
        <Table className='DispositionDetails__corrections-table'>
          <TableHeader>
            <TableHeaderItem>{t('agencies.dispositions.date')}</TableHeaderItem>
            <TableHeaderItem>
              {t('agencies.dispositions.corrections.percentChange')}
            </TableHeaderItem>
            <TableHeaderItem>
              {t('agencies.dispositions.corrections.originalPrice')}
            </TableHeaderItem>
            <TableHeaderItem>
              {t('agencies.dispositions.corrections.correctedPrice')}
            </TableHeaderItem>
            <TableHeaderItem>{t('agencies.dispositions.corrections.reason')}</TableHeaderItem>
            <TableHeaderItem>{t('common.status')}</TableHeaderItem>
            <TableHeaderItem />
          </TableHeader>

          <TableBody>
            <>{correctionRows}</>
          </TableBody>
        </Table>
      )}

      <CorrectionModal
        disposition={disposition}
        correction={editedCorrection}
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        onSaved={onChanged}
      />
    </div>
  )
}

export default DispositionDetails
